import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { 
  Users, 
  Mail, 
  Settings, 
  UserPlus, 
  Crown,
  Shield,
  Activity,
  CreditCard,
  X,
  Check,
  Clock,
  Loader2
} from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
// Removed LoadingSpinner import - using Loader2 from lucide-react
import { useAuth } from "@/hooks/use-auth";
import { TeamActivityFeed } from "@/components/team/activity/team-activity-feed";
import { SeatPurchaseModal } from "@/components/team/SeatPurchaseModal";
import { useQueryClient } from "@tanstack/react-query";

interface TeamMember {
  id: string;
  user_id: number;
  team_id: string;
  role: 'owner' | 'assistant';
  joined_at: string;
  user_name: string;
  user_email: string;
}

interface TeamInvitation {
  id: string;
  email: string;
  role: 'assistant';
  expires_at: string;
  created_at: string;
}

interface Team {
  id: string;
  name: string;
  owner_id: number;
  member_limit: number;
  subscription_status?: string;
  subscription_plan?: string;
  subscription_seats?: number;
  purchased_seats?: number;
  created_at: string;
}

export default function TeamManagement() {
  const [, setLocation] = useLocation();
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<'assistant'>('assistant');
  const [isOwner, setIsOwner] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [seatPurchaseModalOpen, setSeatPurchaseModalOpen] = useState(false);

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      
      // Get current user info from auth context
      if (!user) {
        console.error('No user in auth context');
        return;
      }
      setCurrentUserId(user.id);
      
      // Get team info
      const teamResponse = await api.get('/api/teams/my-team');
      if (teamResponse.data) {
        setTeam(teamResponse.data);
        setIsOwner(teamResponse.data.owner_id === user.id);
        
        // Get team members
        const membersResponse = await api.get(`/api/teams/${teamResponse.data.id}/members`);
        setMembers(membersResponse.data);
        
        // Get pending invitations
        if (teamResponse.data.owner_id === user.id) {
          const invitesResponse = await api.get(`/api/teams/${teamResponse.data.id}/invitations`);
          setInvitations(invitesResponse.data);
        }
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      // If no team exists, show create team UI
    } finally {
      setLoading(false);
    }
  };

  const createTeam = async (teamName: string) => {
    try {
      const response = await api.post('/api/teams/create', { name: teamName });
      setTeam(response.data);
      setIsOwner(true);
      
      // Update user in auth context with team_id
      if (user) {
        updateUser({ ...user, team_id: response.data.id });
      }
      
      toast({
        title: "Success",
        description: "Team created successfully!",
      });
      loadTeamData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to create team',
        variant: "destructive",
      });
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail || !team) return;
    
    try {
      await api.post(`/api/teams/${team.id}/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      toast({
        title: "Success",
        description: `Invitation sent to ${inviteEmail}`,
      });
      setInviteEmail("");
      loadTeamData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to send invitation',
        variant: "destructive",
      });
    }
  };

  const updateMemberRole = async (memberId: string, userId: number, newRole: 'assistant') => {
    if (!team) return;
    
    try {
      await api.put(`/api/teams/${team.id}/members/${userId}`, { role: newRole });
      toast({
        title: "Success",
        description: "Member role updated",
      });
      loadTeamData();
    } catch (error: any) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : (error.response?.data?.detail?.[0]?.msg || 'Failed to update member role');
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };


  const removeMember = async (userId: number) => {
    if (!team || !confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await api.delete(`/api/teams/${team.id}/members/${userId}`);
      toast({
        title: "Success",
        description: "Member removed from team",
      });
      loadTeamData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to remove member',
        variant: "destructive",
      });
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    if (!team) return;
    
    try {
      await api.delete(`/api/teams/${team.id}/invitations/${invitationId}`);
      toast({
        title: "Success",
        description: "Invitation cancelled",
      });
      loadTeamData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to cancel invitation',
        variant: "destructive",
      });
    }
  };

  const navigateToBilling = () => {
    setLocation('/pricing?team=true');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!team) {
    return <CreateTeamView onCreateTeam={createTeam} />;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Team Management</h1>
        <p className="text-muted-foreground">
          Manage your team members, permissions, and settings
        </p>
      </div>

      <div className="grid gap-6 mb-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>{team.name}</CardTitle>
                  <CardDescription>
                    {members.filter(m => m.role !== 'owner').length} assistant{members.filter(m => m.role !== 'owner').length !== 1 ? 's' : ''} / {team.purchased_seats || 0} purchased seat{team.purchased_seats !== 1 ? 's' : ''}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {team.purchased_seats === 0 && (
                  <Badge variant="secondary" className="text-orange-600">
                    No Seats Purchased
                  </Badge>
                )}
                <Button onClick={() => setSeatPurchaseModalOpen(true)} size="sm">
                  <CreditCard className="h-4 w-4 mr-2" />
                  {team.purchased_seats === 0 ? 'Purchase Seats' : 'Add More Seats'}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="members" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>
                Manage your team members and their permissions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {members.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <Avatar>
                      <AvatarFallback>
                        {member.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{member.user_name}</p>
                        {member.role === 'owner' && (
                          <Crown className="h-4 w-4 text-yellow-500" />
                        )}
                        {member.role === 'admin' && (
                          <Shield className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{member.user_email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {isOwner && member.role !== 'owner' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(member.user_id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          {isOwner && (
            <Card>
              <CardHeader>
                <CardTitle>Invite Team Members</CardTitle>
                <CardDescription>
                  {team.purchased_seats === 0
                    ? "Purchase assistant seats to invite team members"
                    : members.filter(m => m.role !== 'owner').length + invitations.length >= (team.purchased_seats || 0)
                    ? `All seats are occupied. You have ${members.filter(m => m.role !== 'owner').length} assistant(s) and ${invitations.length} pending invitation(s).`
                    : "Send invitations to add new members to your team"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {team.purchased_seats === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">
                      You need to purchase assistant seats before inviting team members.
                    </p>
                    <Button onClick={() => setSeatPurchaseModalOpen(true)}>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Purchase Seats
                    </Button>
                  </div>
                ) : members.filter(m => m.role !== 'owner').length + invitations.length >= (team.purchased_seats || 0) ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">
                      All {team.purchased_seats} assistant seat{team.purchased_seats !== 1 ? 's are' : ' is'} occupied.
                    </p>
                    <Button onClick={() => setSeatPurchaseModalOpen(true)}>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Add More Seats
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="colleague@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={inviteMember} disabled={!inviteEmail}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Send Invite
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Pending Invitations</CardTitle>
              <CardDescription>
                Invitations that haven't been accepted yet
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No pending invitations
                </p>
              ) : (
                <div className="space-y-4">
                  {invitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Mail className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{invitation.email}</p>
                          <p className="text-sm text-muted-foreground">
                            Invited {format(new Date(invitation.created_at), 'MMM d, yyyy')} • 
                            Expires {format(new Date(invitation.expires_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{invitation.role}</Badge>
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => cancelInvitation(invitation.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Team Settings</CardTitle>
              <CardDescription>
                Configure your team preferences and billing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="team-name">Team Name</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="team-name"
                    value={team.name}
                    disabled={!isOwner}
                  />
                  {isOwner && (
                    <Button variant="outline">Update</Button>
                  )}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Subscription Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Purchased Seats</span>
                    <span className="text-sm font-medium">
                      {team.purchased_seats || 0} seat{team.purchased_seats !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Seat Usage</span>
                    <span className="text-sm font-medium">
                      {members.filter(m => m.role !== 'owner').length} / {team.purchased_seats || 0} occupied
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Available Seats</span>
                    <span className="text-sm font-medium">
                      {Math.max(0, (team.purchased_seats || 0) - members.filter(m => m.role !== 'owner').length)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Monthly Cost</span>
                    <span className="text-sm font-medium">
                      ${(team.purchased_seats || 0) * 10}/month
                    </span>
                  </div>
                </div>
                {isOwner && (
                  <Button onClick={() => setSeatPurchaseModalOpen(true)} className="mt-4 w-full">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Manage Subscription
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          <TeamActivityFeed teamId={team.id} />
        </TabsContent>
      </Tabs>

      <SeatPurchaseModal
        open={seatPurchaseModalOpen}
        onOpenChange={setSeatPurchaseModalOpen}
        teamId={team.id}
        currentSeats={team.purchased_seats || 0}
        onSuccess={() => {
          loadTeamData();
          setSeatPurchaseModalOpen(false);
        }}
      />
    </div>
  );
}

function CreateTeamView({ onCreateTeam }: { onCreateTeam: (name: string) => void }) {
  const [teamName, setTeamName] = useState("");

  return (
    <div className="container mx-auto py-8 px-4 max-w-2xl">
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto p-4 bg-primary/10 rounded-full w-fit mb-4">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Create Your Team</CardTitle>
          <CardDescription className="text-base">
            Collaborate with your coaching staff by creating a team
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="team-name">Team Name</Label>
            <Input
              id="team-name"
              placeholder="e.g., University Soccer Recruiting"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
            />
          </div>
          
          <div className="space-y-4">
            <h3 className="font-medium">What you'll get with a team:</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-sm">Share player database across your coaching staff</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-sm">Collaborative shortlists and recruiting boards</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-sm">Individual email management with shared player data</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5" />
                <span className="text-sm">Team activity tracking and audit logs</span>
              </li>
            </ul>
          </div>
          
          <Button 
            onClick={() => onCreateTeam(teamName)} 
            disabled={!teamName}
            className="w-full"
            size="lg"
          >
            Create Team
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}